// src/hooks/useAppVersionPoller.js
// Detects when a newer build has been deployed while the app is open in a tab,
// and reloads to pick it up. Uses CRA's build/asset-manifest.json (emitted on
// every build, served no-cache) as the version signal: its "main.js" entry is
// content-hashed, so it changes on every deploy. The baseline is captured on
// mount — combined with the no-cache index.html header, the running app is the
// latest at load time, so the baseline is accurate.
import React from "react";

const MANIFEST_URL = `${process.env.PUBLIC_URL || ""}/asset-manifest.json`;
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

async function fetchBuildSignature() {
  // Cache-bust defensively so a stale CDN/browser copy can't hide a new build.
  const res = await fetch(`${MANIFEST_URL}?_=${Date.now()}`, {
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`asset-manifest ${res.status}`);
  const data = await res.json();
  return data?.files?.["main.js"] || data?.files?.["main.css"] || null;
}

export default function useAppVersionPoller() {
  React.useEffect(() => {
    // Only meaningful for a deployed build; dev has no stable asset manifest.
    if (process.env.NODE_ENV !== "production") return undefined;

    let cancelled = false;
    let baseline = null;
    let pendingReload = false;
    let reloaded = false;
    let timer = null;

    const reloadNow = () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    };

    const check = async () => {
      try {
        const signature = await fetchBuildSignature();
        if (cancelled || !signature) return;
        if (baseline == null) {
          baseline = signature;
          return;
        }
        if (signature !== baseline) {
          pendingReload = true;
          // Reload at a non-disruptive moment: silently if the tab is in the
          // background now, otherwise the visibility handler reloads it the
          // next time the user leaves and returns to the tab.
          if (document.visibilityState === "hidden") reloadNow();
        }
      } catch {
        // Offline or transient failure — try again on the next tick.
      }
    };

    const handleVisibility = () => {
      if (pendingReload) {
        reloadNow();
        return;
      }
      if (document.visibilityState === "visible") check();
    };

    check();
    timer = setInterval(check, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);
}
